import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import landingTranslations from './landing';
import cookieTranslations from './cookie';
import pricingTranslations from './pricing';
import decisionGraphTranslations from './decisionGraph';

// All supported languages with metadata
export const SUPPORTED_LANGUAGES = [
  { code: 'en', name: 'English', nativeName: 'English', dir: 'ltr', currency: 'USD', symbol: '$' },
  { code: 'es', name: 'Spanish', nativeName: 'Espanol', dir: 'ltr', currency: 'EUR', symbol: '$' },
  { code: 'fr', name: 'French', nativeName: 'Francais', dir: 'ltr', currency: 'EUR', symbol: '$' },
  { code: 'de', name: 'German', nativeName: 'Deutsch', dir: 'ltr', currency: 'EUR', symbol: '$' },
  { code: 'it', name: 'Italian', nativeName: 'Italiano (English fallback)', dir: 'ltr', currency: 'EUR', symbol: '$' },
  { code: 'pt', name: 'Portuguese', nativeName: 'Portugues (English fallback)', dir: 'ltr', currency: 'BRL', symbol: 'R$' },
  { code: 'nl', name: 'Dutch', nativeName: 'Nederlands (English fallback)', dir: 'ltr', currency: 'EUR', symbol: '$' },
  { code: 'pl', name: 'Polish', nativeName: 'Polski (English fallback)', dir: 'ltr', currency: 'PLN', symbol: 'zl' },
  { code: 'ru', name: 'Russian', nativeName: 'Russkij (English fallback)', dir: 'ltr', currency: 'RUB', symbol: 'rub.' },
  { code: 'uk', name: 'Ukrainian', nativeName: 'Ukrainska (English fallback)', dir: 'ltr', currency: 'UAH', symbol: 'hrn.' },
  { code: 'zh', name: 'Chinese', nativeName: '中文', dir: 'ltr', currency: 'CNY', symbol: 'Y' },
  { code: 'ja', name: 'Japanese', nativeName: '日本語', dir: 'ltr', currency: 'JPY', symbol: 'Y' },
  { code: 'ko', name: 'Korean', nativeName: 'Hangugeo (English fallback)', dir: 'ltr', currency: 'KRW', symbol: 'W' },
  { code: 'ar', name: 'Arabic', nativeName: 'al-Arabiyya', dir: 'rtl', currency: 'SAR', symbol: 'r.s.' },
  { code: 'he', name: 'Hebrew', nativeName: 'Ivrit (Arabic fallback)', dir: 'rtl', currency: 'ILS', symbol: 'sh.' },
  { code: 'hi', name: 'Hindi', nativeName: 'Hindi (English fallback)', dir: 'ltr', currency: 'INR', symbol: 'Rs' },
  { code: 'bn', name: 'Bengali', nativeName: 'Bangla (English fallback)', dir: 'ltr', currency: 'BDT', symbol: 'Tk' },
  { code: 'tr', name: 'Turkish', nativeName: 'Turkce (English fallback)', dir: 'ltr', currency: 'TRY', symbol: 'TL' },
  { code: 'vi', name: 'Vietnamese', nativeName: 'Tieng Viet (English fallback)', dir: 'ltr', currency: 'VND', symbol: 'D' },
  { code: 'th', name: 'Thai', nativeName: 'Phaasaaathai (English fallback)', dir: 'ltr', currency: 'THB', symbol: 'B' },
  { code: 'id', name: 'Indonesian', nativeName: 'Bahasa Indonesia (English fallback)', dir: 'ltr', currency: 'IDR', symbol: 'Rp' },
  { code: 'ms', name: 'Malay', nativeName: 'Bahasa Melayu (English fallback)', dir: 'ltr', currency: 'MYR', symbol: 'RM' },
  { code: 'sv', name: 'Swedish', nativeName: 'Svenska (English fallback)', dir: 'ltr', currency: 'SEK', symbol: 'kr' },
  { code: 'no', name: 'Norwegian', nativeName: 'Norsk (English fallback)', dir: 'ltr', currency: 'NOK', symbol: 'kr' },
  { code: 'da', name: 'Danish', nativeName: 'Dansk (English fallback)', dir: 'ltr', currency: 'DKK', symbol: 'kr' },
  { code: 'fi', name: 'Finnish', nativeName: 'Suomi (English fallback)', dir: 'ltr', currency: 'EUR', symbol: '$' },
  { code: 'cs', name: 'Czech', nativeName: 'Cestina (English fallback)', dir: 'ltr', currency: 'CZK', symbol: 'Kc' },
];

export const RTL_LANGUAGES = SUPPORTED_LANGUAGES.filter(l => l.dir === 'rtl').map(l => l.code);

// English translation bundle (primary)
const enTranslation = {
  nav: {
    optimize: 'Optimize',
    analytics: 'Savings Report',
    workflows: 'Workflows',
    orchestrate: 'Orchestrate',
    examples: 'Code Examples',
    team: 'Team',
    devPortal: 'Dev Portal',
    models: 'Models',
    billing: 'Billing',
    settings: 'Settings',
  },
  dashboard: {
    totalSpend: 'Total Spend',
    totalSaved: 'Total Saved',
    speedBoost: 'Speed Boost',
    optimizeButton: 'Optimize My Workflow',
    deepOptimize: 'Deep Optimization (takes longer, saves more)',
    noWorkflow: 'No workflow to optimize. Create one first.',
    createDemo: 'Create Demo Workflow',
  },
  optimization: {
    analyzing: 'Analyzing your workflow...',
    finding: 'Finding the best solution...',
    almostDone: 'Almost done...',
    complete: 'Done! You just saved {{amount}}',
    costSaved: 'Cost Saved',
    speedBoost: 'Speed Boost',
    agentReassignments: 'Agent Re-assignments',
    results: 'Results',
    optimizeAgain: 'Optimize Again',
  },
  agents: {
    gradeA: 'Excellent - Running efficiently',
    gradeB: 'Good - Minor improvements possible',
    gradeC: 'Fair - Could save you {{amount}}/month',
    gradeD: 'Needs attention - Wasting {{amount}}/month',
  },
  billing: {
    savingsRate: 'Your savings rate',
    qnnealFee: 'QNNEAL fee (10% of savings)',
    netSavings: 'Your net savings',
    freePlan: 'Free',
    starterPlan: 'Starter',
    teamPlan: 'Team',
    enterprisePlan: 'Enterprise',
    roiCalculator: 'ROI Calculator',
    monthlySpend: 'Monthly LLM Spend',
    estimatedSavings: 'Estimated Monthly Savings',
    creditBalance: 'Credit Balance',
  },
  gateway: {
    title: 'LLM Gateway',
    description: 'Route your LLM requests through QNNEAL for automatic optimization',
    byok: 'Bring Your Own Key',
    managed: 'QNNEAL Managed',
    apiKey: 'API Key',
    endpoint: 'Gateway Endpoint',
    models: 'Available Models',
    requests: 'Total Requests',
    savings: 'Total Savings',
    status: 'Status',
    active: 'Active',
    inactive: 'Inactive',
    provisioningHub: 'Model Provisioning Hub',
  },
  onboarding: {
    welcome: 'Welcome to QNNEAL',
    step1Title: 'Connect Your LLM Provider',
    step1Desc: 'Bring your own API keys or use QNNEAL managed models',
    step2Title: 'Create a Workflow',
    step2Desc: 'Define your AI agent pipeline',
    step3Title: 'Start Saving',
    step3Desc: 'QNNEAL optimizes routing to cut costs by 30-60%',
    byokPath: 'I have my own API keys',
    managedPath: 'Use QNNEAL managed models',
  },
  common: {
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    edit: 'Edit',
    create: 'Create',
    loading: 'Loading...',
    error: 'Something went wrong. We\'re on it.',
    success: 'Success!',
    confirm: 'Confirm',
    close: 'Close',
    search: 'Search',
    filter: 'Filter',
    export: 'Export',
    import: 'Import',
    copy: 'Copy',
    copied: 'Copied!',
    theme: 'Theme',
    language: 'Language',
    logout: 'Log Out',
  },
  security: {
    encryptedKey: 'Key is encrypted and stored securely',
    maskedToken: 'Token masked for security',
  },
  knowledge: {
    page_title: 'Knowledge Base',
    tabs: { documents: 'Documents', upload: 'Upload', search: 'Search', analytics: 'Analytics' },
    upload: { title: 'Upload Document', drag_drop: 'Drag and drop files here, or click to browse', supported_formats: 'Supported: PDF, DOCX, Markdown, TXT, CSV, HTML, PPTX (max 50MB)', uploading: 'Uploading...', processing: 'Processing Status', success: 'Uploaded', error: 'Upload failed' },
    documents: { empty: 'No documents uploaded yet', empty_description: 'Upload your first document to start building your knowledge base', filter_all: 'All Status', filter_type: 'All Types', delete: 'Delete', delete_confirm: 'Are you sure you want to delete this document?' },
    search: { placeholder: 'Search your knowledge base...', no_results: 'No matching chunks found' },
    analytics: { kb_size: 'Knowledge Base', rag_savings: 'RAG Net Savings', retrieval_performance: 'Retrievals', avg_latency: 'Avg Latency', daily_activity: 'Daily Activity', documents: 'Documents', chunks: 'Chunks' },
  },
  login: {
    welcome: 'Welcome to QNNEAL',
    subtitle: 'Sign in to access the Agentic AI Quantum Optimizer',
    quickDemo: 'Quick Demo Access',
    orSignIn: 'Or sign in',
    continueGoogle: 'Continue with Google',
    terms: 'By signing in, you agree to our Terms of Service and Privacy Policy.',
    demoSuccess: 'Welcome to QNNEAL Demo!',
    demoFailed: 'Test login failed',
  },
};

// Spanish translations
const esTranslation = {
  nav: { optimize: 'Optimizar', analytics: 'Informe de Ahorros', workflows: 'Flujos de Trabajo', orchestrate: 'Orquestar', examples: 'Ejemplos de Codigo', team: 'Equipo', devPortal: 'Portal de Desarrollo', models: 'Modelos', billing: 'Facturacion', settings: 'Configuracion', knowledgeBase: 'Base de Conocimiento', playground: 'Zona de Pruebas' },
  dashboard: { totalSpend: 'Gasto Total', totalSaved: 'Total Ahorrado', speedBoost: 'Aceleracion', thisMonth: 'este mes', workflowDag: 'DAG del Flujo', optimizationControls: 'Controles de Optimizacion', agentHealth: 'Salud del Agente', recentActivity: 'Actividad Reciente', optimizeButton: 'Optimizar Mi Flujo', deepOptimize: 'Optimizacion Profunda (tarda mas, ahorra mas)', noWorkflow: 'Sin flujo para optimizar. Crea uno primero.', createDemo: 'Crear Flujo de Demostracion' },
  optimization: { analyzing: 'Analizando tu flujo...', finding: 'Encontrando la mejor solucion...', almostDone: 'Casi listo...', complete: 'Listo! Ahorraste {{amount}}', costSaved: 'Costo Ahorrado', speedBoost: 'Aceleracion', agentReassignments: 'Reasignaciones de Agentes', results: 'Resultados', optimizeAgain: 'Optimizar de Nuevo' },
  agents: { gradeA: 'Excelente - Funcionando eficientemente', gradeB: 'Bueno - Mejoras menores posibles', gradeC: 'Regular - Podria ahorrarte {{amount}}/mes', gradeD: 'Necesita atencion - Desperdiciando {{amount}}/mes' },
  billing: { savingsRate: 'Tu tasa de ahorro', qnnealFee: 'Tarifa QNNEAL (10% de ahorros)', netSavings: 'Tus ahorros netos', freePlan: 'Gratis', starterPlan: 'Inicial', teamPlan: 'Equipo', enterprisePlan: 'Empresa', roiCalculator: 'Calculadora de ROI', monthlySpend: 'Gasto Mensual en LLM', estimatedSavings: 'Ahorros Mensuales Estimados' },
  status: { runningEfficiently: 'Funcionando eficientemente', minorImprovements: 'Mejoras menores posibles', wasting: 'Desperdiciando' },
  common: { save: 'Guardar', cancel: 'Cancelar', delete: 'Eliminar', edit: 'Editar', create: 'Crear', loading: 'Cargando...', error: 'Algo salio mal.', success: 'Exito!', confirm: 'Confirmar', close: 'Cerrar', search: 'Buscar', filter: 'Filtrar', export: 'Exportar', copy: 'Copiar', copied: 'Copiado!', theme: 'Tema', language: 'Idioma', logout: 'Cerrar Sesion' },
  knowledge: {
    page_title: 'Base de Conocimiento',
    tabs: { documents: 'Documentos', upload: 'Subir', search: 'Buscar', analytics: 'Analiticas' },
    upload: { title: 'Subir Documento', drag_drop: 'Arrastra archivos aqui o haz clic', supported_formats: 'Formatos: PDF, DOCX, Markdown, TXT, CSV, HTML, PPTX (max 50MB)', uploading: 'Subiendo...', processing: 'Estado de Procesamiento', success: 'Subido', error: 'Error al subir' },
    documents: { empty: 'Aun no hay documentos', empty_description: 'Sube tu primer documento para construir tu base de conocimiento', filter_all: 'Todos los Estados', filter_type: 'Todos los Tipos', delete: 'Eliminar', delete_confirm: 'Estas seguro de que deseas eliminar este documento?' },
    search: { placeholder: 'Busca en tu base de conocimiento...', no_results: 'No se encontraron resultados' },
    analytics: { kb_size: 'Base de Conocimiento', rag_savings: 'Ahorros RAG', retrieval_performance: 'Recuperaciones', avg_latency: 'Latencia Promedio', daily_activity: 'Actividad Diaria', documents: 'Documentos', chunks: 'Fragmentos' },
  },
  login: {
    welcome: 'Bienvenido a QNNEAL',
    subtitle: 'Inicia sesion para acceder al Optimizador de IA Cuantico',
    quickDemo: 'Acceso Rapido a Demo',
    orSignIn: 'O inicia sesion',
    continueGoogle: 'Continuar con Google',
    terms: 'Al iniciar sesion, aceptas nuestros Terminos de Servicio y Politica de Privacidad.',
    demoSuccess: 'Bienvenido a la Demo de QNNEAL!',
    demoFailed: 'Error en el inicio de sesion de prueba',
  },
};

// French translations
const frTranslation = {
  nav: { optimize: 'Optimiser', analytics: 'Rapport d\'Economies', workflows: 'Flux de Travail', orchestrate: 'Orchestrer', examples: 'Exemples de Code', team: 'Equipe', devPortal: 'Portail Developpeur', models: 'Modeles', billing: 'Facturation', settings: 'Parametres', knowledgeBase: 'Base de Connaissances', playground: 'Bac a Sable' },
  dashboard: { totalSpend: 'Depenses Totales', totalSaved: 'Total Economise', speedBoost: 'Acceleration', thisMonth: 'ce mois-ci', workflowDag: 'DAG du Flux', optimizationControls: 'Controles d\'Optimisation', agentHealth: 'Sante de l\'Agent', recentActivity: 'Activite Recente', optimizeButton: 'Optimiser Mon Flux', deepOptimize: 'Optimisation Profonde (plus long, plus d\'economies)', noWorkflow: 'Aucun flux a optimiser. Creez-en un d\'abord.', createDemo: 'Creer un Flux de Demo' },
  optimization: { analyzing: 'Analyse de votre flux...', finding: 'Recherche de la meilleure solution...', almostDone: 'Presque termine...', complete: 'Termine! Vous avez economise {{amount}}', costSaved: 'Cout Economise', speedBoost: 'Acceleration', agentReassignments: 'Reassignations d\'Agents', results: 'Resultats', optimizeAgain: 'Optimiser a Nouveau' },
  agents: { gradeA: 'Excellent - Fonctionne efficacement', gradeB: 'Bon - Ameliorations mineures possibles', gradeC: 'Moyen - Pourrait vous faire economiser {{amount}}/mois', gradeD: 'A ameliorer - Gaspille {{amount}}/mois' },
  billing: { savingsRate: 'Votre taux d\'economie', qnnealFee: 'Frais QNNEAL (10% des economies)', netSavings: 'Vos economies nettes', freePlan: 'Gratuit', starterPlan: 'Debutant', teamPlan: 'Equipe', enterprisePlan: 'Entreprise', roiCalculator: 'Calculateur de ROI', monthlySpend: 'Depenses Mensuelles LLM', estimatedSavings: 'Economies Mensuelles Estimees' },
  status: { runningEfficiently: 'Fonctionne efficacement', minorImprovements: 'Ameliorations mineures possibles', wasting: 'Gaspille' },
  common: { save: 'Enregistrer', cancel: 'Annuler', delete: 'Supprimer', edit: 'Modifier', create: 'Creer', loading: 'Chargement...', error: 'Une erreur est survenue.', success: 'Succes!', confirm: 'Confirmer', close: 'Fermer', search: 'Rechercher', filter: 'Filtrer', export: 'Exporter', copy: 'Copier', copied: 'Copie!', theme: 'Theme', language: 'Langue', logout: 'Deconnexion' },
  knowledge: {
    page_title: 'Base de Connaissances',
    tabs: { documents: 'Documents', upload: 'Telecharger', search: 'Rechercher', analytics: 'Analytique' },
    upload: { title: 'Telecharger un Document', drag_drop: 'Glissez les fichiers ici ou cliquez pour parcourir', supported_formats: 'Formats: PDF, DOCX, Markdown, TXT, CSV, HTML, PPTX (max 50 Mo)', uploading: 'Telechargement...', processing: 'Etat du Traitement', success: 'Telecharge', error: 'Echec du telechargement' },
    documents: { empty: 'Aucun document telecharge', empty_description: 'Telechargez votre premier document pour construire votre base de connaissances', filter_all: 'Tous les Etats', filter_type: 'Tous les Types', delete: 'Supprimer', delete_confirm: 'Etes-vous sur de vouloir supprimer ce document?' },
    search: { placeholder: 'Rechercher dans votre base de connaissances...', no_results: 'Aucun resultat' },
    analytics: { kb_size: 'Base de Connaissances', rag_savings: 'Economies RAG', retrieval_performance: 'Recuperations', avg_latency: 'Latence Moyenne', daily_activity: 'Activite Quotidienne', documents: 'Documents', chunks: 'Fragments' },
  },
};

// German translations
const deTranslation = {
  nav: { optimize: 'Optimieren', analytics: 'Sparbericht', workflows: 'Workflows', orchestrate: 'Orchestrieren', examples: 'Code-Beispiele', team: 'Team', devPortal: 'Entwicklerportal', models: 'Modelle', billing: 'Abrechnung', settings: 'Einstellungen', knowledgeBase: 'Wissensbasis', playground: 'Spielplatz' },
  dashboard: { totalSpend: 'Gesamtausgaben', totalSaved: 'Gesamt Gespart', speedBoost: 'Beschleunigung', thisMonth: 'diesen Monat', workflowDag: 'Workflow-DAG', optimizationControls: 'Optimierungssteuerung', agentHealth: 'Agenten-Zustand', recentActivity: 'Letzte Aktivitaet', optimizeButton: 'Workflow Optimieren', deepOptimize: 'Tiefenoptimierung (dauert laenger, spart mehr)', noWorkflow: 'Kein Workflow vorhanden. Erstellen Sie zuerst einen.', createDemo: 'Demo-Workflow Erstellen' },
  optimization: { analyzing: 'Workflow wird analysiert...', finding: 'Beste Loesung wird gesucht...', almostDone: 'Fast fertig...', complete: 'Fertig! Sie haben {{amount}} gespart', costSaved: 'Kosten Gespart', speedBoost: 'Beschleunigung', agentReassignments: 'Agenten-Neuzuweisungen', results: 'Ergebnisse', optimizeAgain: 'Erneut Optimieren' },
  agents: { gradeA: 'Ausgezeichnet - Laeuft effizient', gradeB: 'Gut - Kleine Verbesserungen moeglich', gradeC: 'Befriedigend - Koennte {{amount}}/Monat sparen', gradeD: 'Verbesserungsbedarf - Verschwendet {{amount}}/Monat' },
  billing: { savingsRate: 'Ihre Sparquote', qnnealFee: 'QNNEAL-Gebuehr (10% der Ersparnisse)', netSavings: 'Ihre Nettoersparnisse', freePlan: 'Kostenlos', starterPlan: 'Starter', teamPlan: 'Team', enterprisePlan: 'Unternehmen', roiCalculator: 'ROI-Rechner', monthlySpend: 'Monatliche LLM-Ausgaben', estimatedSavings: 'Geschaetzte Monatliche Ersparnisse' },
  status: { runningEfficiently: 'Laeuft effizient', minorImprovements: 'Kleine Verbesserungen moeglich', wasting: 'Verschwendet' },
  common: { save: 'Speichern', cancel: 'Abbrechen', delete: 'Loeschen', edit: 'Bearbeiten', create: 'Erstellen', loading: 'Laden...', error: 'Etwas ist schiefgelaufen.', success: 'Erfolg!', confirm: 'Bestaetigen', close: 'Schliessen', search: 'Suchen', filter: 'Filtern', export: 'Exportieren', copy: 'Kopieren', copied: 'Kopiert!', theme: 'Thema', language: 'Sprache', logout: 'Abmelden' },
  knowledge: {
    page_title: 'Wissensbasis',
    tabs: { documents: 'Dokumente', upload: 'Hochladen', search: 'Suchen', analytics: 'Analytik' },
    upload: { title: 'Dokument Hochladen', drag_drop: 'Dateien hierher ziehen oder klicken', supported_formats: 'Formate: PDF, DOCX, Markdown, TXT, CSV, HTML, PPTX (max 50MB)', uploading: 'Hochladen...', processing: 'Verarbeitungsstatus', success: 'Hochgeladen', error: 'Hochladen fehlgeschlagen' },
    documents: { empty: 'Noch keine Dokumente', empty_description: 'Laden Sie Ihr erstes Dokument hoch', filter_all: 'Alle Status', filter_type: 'Alle Typen', delete: 'Loeschen', delete_confirm: 'Moechten Sie dieses Dokument wirklich loeschen?' },
    search: { placeholder: 'Wissensbasis durchsuchen...', no_results: 'Keine Ergebnisse' },
    analytics: { kb_size: 'Wissensbasis', rag_savings: 'RAG-Ersparnisse', retrieval_performance: 'Abrufe', avg_latency: 'Durchschn. Latenz', daily_activity: 'Taegliche Aktivitaet', documents: 'Dokumente', chunks: 'Fragmente' },
  },
  login: {
    welcome: 'Willkommen bei QNNEAL',
    subtitle: 'Melden Sie sich an, um auf den KI-Quantenoptimierer zuzugreifen',
    quickDemo: 'Schnellzugang Demo',
    orSignIn: 'Oder anmelden',
    continueGoogle: 'Weiter mit Google',
    terms: 'Durch die Anmeldung akzeptieren Sie unsere Nutzungsbedingungen und Datenschutzrichtlinie.',
    demoSuccess: 'Willkommen bei der QNNEAL Demo!',
    demoFailed: 'Test-Anmeldung fehlgeschlagen',
  },
};

// Arabic translations (RTL)
const arTranslation = {
  nav: { optimize: 'Tahsin', analytics: 'Taqrir al-Tawfir', workflows: 'Tadaffuqat al-Amal', orchestrate: 'Tandhim', examples: 'Amthilat al-Kod', team: 'al-Fariq', devPortal: 'Bawwabat al-Mutatawwirin', models: 'al-Namadhij', billing: 'al-Fawatir', settings: 'al-Iadaadaat', knowledgeBase: 'Qaidat al-Maarifa', playground: 'Saahat al-Tajriba' },
  dashboard: { totalSpend: 'Ijmali al-Infaq', totalSaved: 'Ijmali al-Tawfir', speedBoost: 'Tasri al-Suraa', thisMonth: 'hadha al-Shahr', workflowDag: 'Mukhattat al-Tadaffuq', optimizationControls: 'Adawat al-Tahsin', agentHealth: 'Sihat al-Wakil', recentActivity: 'al-Nashat al-Akhir', optimizeButton: 'Tahsin Tadaffuqi', deepOptimize: 'Tahsin Amiq (yastghriq waqtan atwal, yuwaffir akthar)', noWorkflow: 'La yujad tadaffuq lil-tahsin. Anshi wahidan awwalan.', createDemo: 'Insha Tadaffuq Tajribi' },
  optimization: { analyzing: 'Tahlil tadaffuqik...', finding: 'al-Bahth an afdhel hall...', almostDone: 'Takriban intaha...', complete: 'Tamam! Laqad waffart {{amount}}', costSaved: 'al-Taklifa al-Muwaffara', speedBoost: 'Tasri', agentReassignments: 'Iaadat Taayin al-Wukala', results: 'al-Nataaij', optimizeAgain: 'Tahsin Maratan Ukhra' },
  agents: { gradeA: 'Mumtaz - Yamul bi-kafaa', gradeB: 'Jayyid - Tahsinat saghira mumkina', gradeC: 'Mutawassit - Yumkin tawfir {{amount}}/shahr', gradeD: 'Yahtaj ila aamal - Yuhdir {{amount}}/shahr' },
  status: { runningEfficiently: 'Yamul bi-kafaa', minorImprovements: 'Tahsinat saghira mumkina', wasting: 'Yuhdir' },
  common: { save: 'Hifz', cancel: 'Ilgha', delete: 'Hadhf', edit: 'Tadil', create: 'Insha', loading: 'Tahmiil...', error: 'Hadatha khata.', success: 'Najah!', confirm: 'Takid', close: 'Ighlaq', search: 'Bahth', filter: 'Tasfiya', export: 'Tasdir', copy: 'Naskh', copied: 'Tamma al-Naskh!', theme: 'al-Mawdu', language: 'al-Lugha', logout: 'Tasjiil al-Khuruuj' },
  knowledge: {
    page_title: 'Qaidat al-Maarifa',
    tabs: { documents: 'al-Wathaaiq', upload: 'Raf', search: 'Bahth', analytics: 'Tahlilaat' },
    upload: { title: 'Raf Wathiqa', drag_drop: 'Ishab al-malaffaat huna aw inqar lil-tasaffuh', supported_formats: 'al-Sigha: PDF, DOCX, Markdown, TXT, CSV, HTML, PPTX (had aqsa 50MB)', uploading: 'Jariy al-Raf...', processing: 'Halat al-Mualaja', success: 'Tamma al-Raf', error: 'Fashal al-Raf' },
    documents: { empty: 'La tujad wathaaiq bad', empty_description: 'Irfa wathiqatak al-ula libinaai qaidat al-maarifa', filter_all: 'Jamii al-Halat', filter_type: 'Jamii al-Anwaa', delete: 'Hadhf', delete_confirm: 'Hal anta mutaakkid min hadhf hadhihi al-wathiqa?' },
    search: { placeholder: 'Abhath fi qaidat al-maarifa...', no_results: 'La tujad nataaij' },
    analytics: { kb_size: 'Qaidat al-Maarifa', rag_savings: 'Tawfiraat RAG', retrieval_performance: 'Istirdadaat', avg_latency: 'Mutawassit al-Taakhkhur', daily_activity: 'al-Nashat al-Yawmi', documents: 'Wathaaiq', chunks: 'Ajzaa' },
  },
  login: {
    welcome: 'Marhaban bi QNNEAL',
    subtitle: 'Sajjil al-dukhul lil-wusul ila Muhsin al-Dhakaa al-Istinaai al-Kuanti',
    quickDemo: 'Wusul Sarii lil-Istitlaa',
    orSignIn: 'Aw sajjil al-dukhul',
    continueGoogle: 'al-Mutaabaa maa Google',
    terms: 'Bi-tasjiil al-dukhul, anta tuwafiq ala Shurut al-Khidma wa Siyasat al-Khususiyya.',
    demoSuccess: 'Marhaban fi Istitlaa QNNEAL!',
    demoFailed: 'Fashal tasjiil al-dukhul al-tajribi',
  },
};

// Japanese translations (real Japanese characters)
const jaTranslation = {
  nav: { optimize: '最適化', analytics: '節約レポート', workflows: 'ワークフロー', orchestrate: 'オーケストレーション', examples: 'コード例', team: 'チーム', devPortal: '開発者ポータル', models: 'モデル', billing: '請求', settings: '設定', knowledgeBase: 'ナレッジベース', playground: 'プレイグラウンド' },
  dashboard: { totalSpend: '合計支出', totalSaved: '合計節約額', speedBoost: '速度向上', thisMonth: '今月', workflowDag: 'ワークフロー DAG', optimizationControls: '最適化コントロール', agentHealth: 'エージェント状態', recentActivity: '最近のアクティビティ', optimizeButton: 'ワークフローを最適化', deepOptimize: 'ディープ最適化（時間がかかりますが、より節約できます）', noWorkflow: '最適化するワークフローがありません。まず作成してください。', createDemo: 'デモワークフローを作成' },
  optimization: { analyzing: 'ワークフローを分析中...', finding: '最適な解決策を検索中...', almostDone: 'もうすぐ完了...', complete: '完了！{{amount}}節約しました', costSaved: '節約コスト', speedBoost: '速度向上', agentReassignments: 'エージェント再割り当て', results: '結果', optimizeAgain: '再度最適化' },
  agents: { gradeA: '優秀 - 効率的に稼働中', gradeB: '良好 - 軽微な改善の余地あり', gradeC: '普通 - {{amount}}/月 節約可能', gradeD: '要注意 - {{amount}}/月 無駄遣い' },
  billing: { savingsRate: '節約率', qnnealFee: 'QNNEAL手数料（節約額の10%）', netSavings: '純節約額', freePlan: '無料', starterPlan: 'スターター', teamPlan: 'チーム', enterprisePlan: 'エンタープライズ', roiCalculator: 'ROI計算機', monthlySpend: '月間LLM支出', estimatedSavings: '推定月間節約額' },
  status: { runningEfficiently: '効率的に稼働中', minorImprovements: '軽微な改善の余地あり', wasting: '無駄遣い' },
  common: { save: '保存', cancel: 'キャンセル', delete: '削除', edit: '編集', create: '作成', loading: '読み込み中...', error: 'エラーが発生しました。', success: '成功！', confirm: '確認', close: '閉じる', search: '検索', filter: 'フィルター', export: 'エクスポート', copy: 'コピー', copied: 'コピーしました！', theme: 'テーマ', language: '言語', logout: 'ログアウト' },
  knowledge: {
    page_title: 'ナレッジベース',
    tabs: { documents: 'ドキュメント', upload: 'アップロード', search: '検索', analytics: '分析' },
    upload: { title: 'ドキュメントアップロード', drag_drop: 'ファイルをドラッグ＆ドロップ', supported_formats: '対応：PDF, DOCX, Markdown, TXT, CSV, HTML, PPTX（最大50MB）', uploading: 'アップロード中...', processing: '処理状況', success: 'アップロード完了', error: 'アップロード失敗' },
    documents: { empty: 'ドキュメントはまだありません', empty_description: '最初のドキュメントをアップロードしてナレッジベースを構築', filter_all: 'すべてのステータス', filter_type: 'すべてのタイプ', delete: '削除', delete_confirm: 'このドキュメントを削除してもよろしいですか？' },
    search: { placeholder: 'ナレッジベースを検索...', no_results: '結果が見つかりません' },
    analytics: { kb_size: 'ナレッジベース', rag_savings: 'RAG節約', retrieval_performance: '取得', avg_latency: '平均レイテンシ', daily_activity: '日別アクティビティ', documents: 'ドキュメント', chunks: 'チャンク' },
  },
  login: {
    welcome: 'QNNEALへようこそ',
    subtitle: 'エージェントAI量子オプティマイザにアクセスするにはサインインしてください',
    quickDemo: 'クイックデモアクセス',
    orSignIn: 'またはサインイン',
    continueGoogle: 'Googleで続行',
    terms: 'サインインすることで、利用規約とプライバシーポリシーに同意します。',
    demoSuccess: 'QNNEALデモへようこそ！',
    demoFailed: 'テストログインに失敗しました',
  },
};

// Chinese Simplified translations (real Chinese characters)
const zhTranslation = {
  nav: { optimize: '优化', analytics: '节省报告', workflows: '工作流', orchestrate: '编排', examples: '代码示例', team: '团队', devPortal: '开发者门户', models: '模型', billing: '账单', settings: '设置', knowledgeBase: '知识库', playground: '试验场' },
  dashboard: { totalSpend: '总支出', totalSaved: '总节省', speedBoost: '速度提升', thisMonth: '本月', workflowDag: '工作流 DAG', optimizationControls: '优化控制', agentHealth: '代理状态', recentActivity: '最近活动', optimizeButton: '优化我的工作流', deepOptimize: '深度优化（耗时更长，节省更多）', noWorkflow: '没有工作流可优化。请先创建一个。', createDemo: '创建演示工作流' },
  optimization: { analyzing: '正在分析工作流...', finding: '正在寻找最佳方案...', almostDone: '即将完成...', complete: '完成！您节省了{{amount}}', costSaved: '节省成本', speedBoost: '速度提升', agentReassignments: '代理重新分配', results: '结果', optimizeAgain: '再次优化' },
  agents: { gradeA: '优秀 - 高效运行', gradeB: '良好 - 可稍微改进', gradeC: '一般 - 可节省{{amount}}/月', gradeD: '需关注 - 浪费{{amount}}/月' },
  billing: { savingsRate: '节省率', qnnealFee: 'QNNEAL费用（节省额的10%）', netSavings: '净节省额', freePlan: '免费', starterPlan: '入门', teamPlan: '团队', enterprisePlan: '企业', roiCalculator: 'ROI计算器', monthlySpend: '月度LLM支出', estimatedSavings: '预计月度节省' },
  status: { runningEfficiently: '高效运行', minorImprovements: '可稍微改进', wasting: '浪费' },
  common: { save: '保存', cancel: '取消', delete: '删除', edit: '编辑', create: '创建', loading: '加载中...', error: '出现错误。', success: '成功！', confirm: '确认', close: '关闭', search: '搜索', filter: '筛选', export: '导出', copy: '复制', copied: '已复制！', theme: '主题', language: '语言', logout: '退出登录' },
  knowledge: {
    page_title: '知识库',
    tabs: { documents: '文档', upload: '上传', search: '搜索', analytics: '分析' },
    upload: { title: '上传文档', drag_drop: '拖拽文件到此处或点击选择', supported_formats: '支持：PDF, DOCX, Markdown, TXT, CSV, HTML, PPTX（最大50MB）', uploading: '上传中...', processing: '处理状态', success: '上传成功', error: '上传失败' },
    documents: { empty: '尚无文档', empty_description: '上传第一个文档来构建知识库', filter_all: '所有状态', filter_type: '所有类型', delete: '删除', delete_confirm: '确认要删除此文档吗？' },
    search: { placeholder: '搜索知识库...', no_results: '未找到结果' },
    analytics: { kb_size: '知识库', rag_savings: 'RAG节省', retrieval_performance: '检索', avg_latency: '平均延迟', daily_activity: '每日活动', documents: '文档', chunks: '块' },
  },
  login: {
    welcome: '欢迎使用QNNEAL',
    subtitle: '登录以访问代理AI量子优化器',
    quickDemo: '快速演示访问',
    orSignIn: '或登录',
    continueGoogle: '使用Google继续',
    terms: '登录即表示您同意我们的服务条款和隐私政策。',
    demoSuccess: '欢迎使用QNNEAL演示！',
    demoFailed: '测试登录失败',
  },
};

// Helper to merge landing translations into a base bundle
function withLanding(base, langCode) {
  return {
    ...base,
    landing: landingTranslations[langCode] || landingTranslations.en,
    cookie: cookieTranslations[langCode] || cookieTranslations.en,
    plans: pricingTranslations[langCode] || pricingTranslations.en,
    decisionGraph: decisionGraphTranslations[langCode] || decisionGraphTranslations.en,
  };
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: withLanding(enTranslation, 'en') },
      es: { translation: withLanding(esTranslation, 'es') },
      fr: { translation: withLanding(frTranslation, 'fr') },
      de: { translation: withLanding(deTranslation, 'de') },
      ar: { translation: withLanding(arTranslation, 'ar') },
      ja: { translation: withLanding(jaTranslation, 'ja') },
      zh: { translation: withLanding(zhTranslation, 'zh') },
      // Languages with landing translations + English app fallback
      it: { translation: withLanding(enTranslation, 'it') },
      pt: { translation: withLanding(enTranslation, 'pt') },
      nl: { translation: withLanding(enTranslation, 'nl') },
      pl: { translation: withLanding(enTranslation, 'pl') },
      ru: { translation: withLanding(enTranslation, 'ru') },
      uk: { translation: withLanding(enTranslation, 'uk') },
      ko: { translation: withLanding(enTranslation, 'ko') },
      he: { translation: withLanding(arTranslation, 'he') },
      hi: { translation: withLanding(enTranslation, 'hi') },
      bn: { translation: withLanding(enTranslation, 'bn') },
      tr: { translation: withLanding(enTranslation, 'tr') },
      vi: { translation: withLanding(enTranslation, 'vi') },
      th: { translation: withLanding(enTranslation, 'th') },
      id: { translation: withLanding(enTranslation, 'id') },
      ms: { translation: withLanding(enTranslation, 'ms') },
      sv: { translation: withLanding(enTranslation, 'sv') },
      no: { translation: withLanding(enTranslation, 'no') },
      da: { translation: withLanding(enTranslation, 'da') },
      fi: { translation: withLanding(enTranslation, 'fi') },
      cs: { translation: withLanding(enTranslation, 'cs') },
    },
    fallbackLng: 'en',
    interpolation: {
      escapeValue: false,
    },
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: 'qn_language',
      caches: ['localStorage'],
    },
  });

// RTL document direction handler
export function applyLanguageDirection(langCode) {
  const isRTL = RTL_LANGUAGES.includes(langCode);
  document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
  document.documentElement.lang = langCode;
}

// Currency formatter based on language
export function getCurrencyFormatter(langCode) {
  const lang = SUPPORTED_LANGUAGES.find(l => l.code === langCode) || SUPPORTED_LANGUAGES[0];
  return {
    currency: lang.currency,
    symbol: lang.symbol,
    format: (amount) => {
      if (amount >= 1000) return `${lang.symbol}${(amount / 1000).toFixed(1)}k`;
      if (amount >= 1) return `${lang.symbol}${amount.toFixed(2)}`;
      return `${lang.symbol}${amount.toFixed(4)}`;
    },
  };
}

export default i18n;
